import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import {
  MapContainer, ImageOverlay, Marker, Popup, useMap, useMapEvents,
} from 'react-leaflet';
import { Button } from './ui/button';
import {
  Star, Crown, MapPin as MapPinIcon, Sparkles, Plus, Trash2, Handshake,
  Loader2, RefreshCw, X,
} from 'lucide-react';
import { SPRITES, RARITIES, VARIANTS } from '../mock';
import { POIS, SPAWN_TYPES, SPRITE_SPAWNS, MAP_SIZE } from '../lib/mapData';
import { useUserPins } from '../hooks/useUserPins';
import { toast } from '../hooks/use-toast';

// Build a small colored DivIcon for sprite pins.
function buildSpritePinIcon(sprite, entry) {
  const rarity = RARITIES[sprite.rarity];
  const variant = VARIANTS[sprite.variant];
  const owned = !!entry?.owned;
  const mastered = !!entry?.mastered;
  const ring = mastered
    ? 'background: conic-gradient(#ff2bd6,#ffd900,#00b8ff,#9d3dff,#ff2bd6);'
    : `background: ${rarity.color};`;
  const dim = owned ? '' : 'filter: grayscale(0.6) opacity(0.85);';
  const html = `
    <div style="position:relative;width:38px;height:46px;">
      <div style="position:absolute;inset:0;${ring}border:2px solid #3a1064;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 0 #3a1064;"></div>
      <div style="position:absolute;top:3px;left:3px;width:32px;height:32px;background:#fff;border:2px solid #3a1064;border-radius:50%;overflow:hidden;display:flex;align-items:center;justify-content:center;${dim}">
        <img src="${sprite.img}" alt="" style="width:28px;height:28px;object-fit:contain;" />
      </div>
      ${mastered ? '<div style="position:absolute;top:-6px;right:-4px;width:16px;height:16px;background:#ffd900;border:2px solid #3a1064;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;color:#3a1064;">★</div>' : ''}
      <div style="position:absolute;bottom:-2px;left:50%;transform:translateX(-50%);width:6px;height:6px;background:#3a1064;border-radius:50%;"></div>
    </div>`;
  return L.divIcon({
    html,
    className: 'sprite-vault-pin',
    iconSize: [38, 46],
    iconAnchor: [19, 44],
    popupAnchor: [0, -38],
  });
}

function buildPoiIcon(poi) {
  const html = `
    <div style="display:flex;align-items:center;gap:6px;background:#fff7d6;border:2px solid #3a1064;padding:3px 8px;border-radius:999px;box-shadow:0 2px 0 #3a1064;font-weight:900;color:#3a1064;font-size:10px;text-transform:uppercase;letter-spacing:0.04em;white-space:nowrap;">
      <span style="width:8px;height:8px;background:#ff2bd6;border:2px solid #3a1064;border-radius:50%;"></span>
      ${poi.name}
    </div>`;
  return L.divIcon({
    html,
    className: 'sprite-vault-poi',
    iconSize: [null, 24],
    iconAnchor: [0, 12],
  });
}

function buildUserPinIcon() {
  const html = `
    <div style="position:relative;width:34px;height:42px;">
      <div style="position:absolute;inset:0;background:#1877f2;border:2px solid #3a1064;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 0 #3a1064;"></div>
      <div style="position:absolute;top:6px;left:50%;transform:translateX(-50%);color:#fff;font-weight:900;font-size:14px;">★</div>
    </div>`;
  return L.divIcon({
    html,
    className: 'sprite-vault-userpin',
    iconSize: [34, 42],
    iconAnchor: [17, 40],
    popupAnchor: [0, -36],
  });
}

// Cluster wrapper using leaflet.markercluster directly (works with React 19).
function MarkerClusterLayer({ markers, getIcon, onClick, makePopup }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (cluster) => {
        const n = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="background:#ff2bd6;color:#fff;border:3px solid #3a1064;border-radius:50%;width:42px;height:42px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:13px;box-shadow:0 3px 0 #3a1064;">${n}</div>`,
          className: 'sprite-vault-cluster',
          iconSize: [42, 42],
        });
      },
    });
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    markers.forEach((m) => {
      const marker = L.marker(m.pos, { icon: getIcon(m) });
      const popupHtml = makePopup(m);
      if (popupHtml) {
        marker.bindPopup(popupHtml, { className: 'sprite-vault-popup', maxWidth: 260 });
      }
      if (onClick) marker.on('click', () => onClick(m));
      group.addLayer(marker);
    });
  }, [markers, getIcon, onClick, makePopup]);

  return null;
}

// User pin drop on map click
function MapClickHandler({ enabled, onDrop }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onDrop([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function SpriteVaultMap({
  mapUrl,
  collection,
  getEntry,
  filters,
  dropMode,
  setDropMode,
  onTrade,
}) {
  const bounds = useMemo(() => [[0, 0], [MAP_SIZE, MAP_SIZE]], []);
  const { pins, addPin, removePin } = useUserPins();
  const [pendingDrop, setPendingDrop] = useState(null);
  const [pinLabel, setPinLabel] = useState('Meet-up spot');
  const [pinNote, setPinNote] = useState('');

  // Build filtered sprite spawn markers
  const spriteMarkers = useMemo(() => {
    return SPRITE_SPAWNS
      .map((sp) => {
        const sprite = SPRITES.find((s) => s.id === sp.spriteId);
        if (!sprite) return null;
        const entry = getEntry(sprite.id) || {};
        // Apply filters
        if (!filters.rarities.has(sprite.rarity)) return null;
        if (!filters.variants.has(sprite.variant)) return null;
        if (!filters.spawnTypes.has(sp.spawnType)) return null;
        const ownedOk =
          (filters.status.has('owned') && entry.owned) ||
          (filters.status.has('missing') && !entry.owned) ||
          (filters.status.has('mastered') && entry.mastered);
        if (!ownedOk) return null;
        return { ...sp, sprite, entry };
      })
      .filter(Boolean);
  }, [filters, getEntry]);

  // Build POI markers — filtered by category checkboxes
  const poiMarkers = useMemo(() => {
    return POIS.filter((p) => filters.poiTypes.has(p.type));
  }, [filters.poiTypes]);

  const handleMapClick = (pos) => {
    setPendingDrop(pos);
    setPinLabel('Meet-up spot');
    setPinNote('');
  };

  const confirmAddPin = () => {
    if (!pendingDrop) return;
    addPin(pendingDrop, pinLabel, pinNote);
    setPendingDrop(null);
    setDropMode(false);
    toast({ title: 'Pin dropped', description: `${pinLabel} added to your map.` });
  };

  const getSpriteIcon = (m) => buildSpritePinIcon(m.sprite, m.entry);
  const getPoiIcon = (p) => buildPoiIcon(p);
  const getUserPinIcon = () => buildUserPinIcon();

  const makeSpritePopup = (m) => {
    const r = RARITIES[m.sprite.rarity];
    const v = VARIANTS[m.sprite.variant];
    const poi = POIS.find((p) => p.id === m.poiId);
    const spawn = SPAWN_TYPES.find((s) => s.id === m.spawnType);
    return `
      <div style="font-family:inherit;min-width:200px;">
        <div style="display:flex;align-items:center;gap:8px;">
          <img src="${m.sprite.img}" alt="" style="width:48px;height:48px;object-fit:contain;background:#fff7d6;border:2px solid #3a1064;border-radius:8px;padding:2px;" />
          <div style="min-width:0;">
            <div style="font-weight:900;color:#3a1064;font-size:14px;line-height:1.1;">${m.sprite.name}</div>
            <div style="display:flex;gap:4px;margin-top:3px;flex-wrap:wrap;">
              <span style="font-size:9px;font-weight:900;text-transform:uppercase;background:${r.color};color:#fff;padding:2px 5px;border-radius:4px;">${r.label}</span>
              <span style="font-size:9px;font-weight:900;text-transform:uppercase;background:${v.color};color:#fff;padding:2px 5px;border-radius:4px;">${v.label}</span>
            </div>
          </div>
        </div>
        <div style="margin-top:8px;display:flex;flex-direction:column;gap:3px;font-size:11px;color:#3a1064;font-weight:700;">
          <div>📍 ${poi ? poi.name : 'Unknown'}</div>
          <div style="display:flex;align-items:center;gap:4px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${spawn?.color || '#999'};border:1.5px solid #3a1064;"></span>
            ${spawn ? spawn.label : 'Spawn'} spawn
          </div>
          <div>${m.entry.owned ? '✅ Owned' : '⬜ Missing'}${m.entry.mastered ? ' · ⭐ Mastered' : ''}${m.entry.level ? ` · Lv ${m.entry.level}` : ''}</div>
        </div>
        <button
          data-sprite-trade-id="${m.sprite.id}"
          style="margin-top:10px;width:100%;background:#ff2bd6;color:#fff;border:2px solid #3a1064;border-radius:8px;padding:6px 8px;font-weight:900;font-size:11px;cursor:pointer;box-shadow:0 2px 0 #3a1064;"
        >🤝 Trade for this sprite</button>
      </div>`;
  };

  const makePoiPopup = (p) => `
    <div style="min-width:160px;color:#3a1064;">
      <div style="font-weight:900;font-size:14px;">${p.name}</div>
      <div style="font-size:11px;font-weight:700;margin-top:2px;text-transform:capitalize;">${p.type}</div>
    </div>`;

  const makeUserPinPopup = (p) => `
    <div style="min-width:170px;color:#3a1064;">
      <div style="font-weight:900;font-size:13px;">${p.label}</div>
      ${p.note ? `<div style="font-size:11px;font-weight:600;margin-top:3px;">${p.note}</div>` : ''}
      <button
        data-userpin-remove="${p.id}"
        style="margin-top:8px;width:100%;background:#ef4444;color:#fff;border:2px solid #3a1064;border-radius:8px;padding:5px 8px;font-weight:900;font-size:11px;cursor:pointer;"
      >Remove pin</button>
    </div>`;

  // Delegate clicks for in-popup buttons (rendered via raw HTML)
  useEffect(() => {
    const handler = (e) => {
      const t = e.target.closest('[data-sprite-trade-id]');
      if (t) {
        const id = Number(t.getAttribute('data-sprite-trade-id'));
        const sprite = SPRITES.find((s) => s.id === id);
        if (sprite && onTrade) onTrade(sprite);
        return;
      }
      const rm = e.target.closest('[data-userpin-remove]');
      if (rm) {
        const id = rm.getAttribute('data-userpin-remove');
        if (window.confirm('Remove this pin?')) {
          removePin(id);
          toast({ title: 'Pin removed' });
        }
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onTrade, removePin]);

  return (
    <div className="relative w-full h-full">
      <MapContainer
        crs={L.CRS.Simple}
        bounds={bounds}
        minZoom={-2}
        maxZoom={3}
        zoom={0}
        zoomSnap={0.25}
        maxBounds={[[-200, -200], [MAP_SIZE + 200, MAP_SIZE + 200]]}
        style={{ width: '100%', height: '100%', background: '#0b0625', cursor: dropMode ? 'crosshair' : '' }}
        attributionControl={false}
      >
        {mapUrl && <ImageOverlay url={mapUrl} bounds={bounds} />}
        <MapClickHandler enabled={dropMode} onDrop={handleMapClick} />

        {/* POI labels — not clustered */}
        {poiMarkers.map((p) => (
          <Marker
            key={p.id}
            position={p.pos}
            icon={getPoiIcon(p)}
            interactive={true}
          >
            <Popup className="sprite-vault-popup">
              <div dangerouslySetInnerHTML={{ __html: makePoiPopup(p) }} />
            </Popup>
          </Marker>
        ))}

        {/* Sprite markers — clustered */}
        <MarkerClusterLayer
          markers={spriteMarkers}
          getIcon={getSpriteIcon}
          makePopup={makeSpritePopup}
        />

        {/* User-dropped pins */}
        {pins.map((p) => (
          <Marker key={p.id} position={p.pos} icon={getUserPinIcon()}>
            <Popup className="sprite-vault-popup">
              <div dangerouslySetInnerHTML={{ __html: makeUserPinPopup(p) }} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {dropMode && !pendingDrop && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-[#ffd900] border-2 border-[#3a1064] rounded-full px-4 py-2 shadow-[0_3px_0_#3a1064] flex items-center gap-2 font-black text-[#3a1064] text-sm">
          <MapPinIcon className="h-4 w-4" /> Click anywhere on the map to drop a pin
          <button
            data-testid="cancel-drop-btn"
            onClick={() => setDropMode(false)}
            className="ml-2 h-6 w-6 rounded-full bg-white border-2 border-[#3a1064] flex items-center justify-center hover:bg-rose-100"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {pendingDrop && (
        <div
          data-testid="confirm-pin-panel"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-white border-[3px] border-[#3a1064] rounded-2xl px-4 py-3 shadow-[0_4px_0_#3a1064] w-[320px]"
        >
          <div className="font-black text-[#3a1064] text-sm mb-2 flex items-center gap-1.5">
            <MapPinIcon className="h-4 w-4 text-[#ff2bd6]" /> Drop a pin here
          </div>
          <input
            data-testid="pin-label-input"
            value={pinLabel}
            onChange={(e) => setPinLabel(e.target.value)}
            placeholder="Label (e.g. Trade meet-up)"
            className="w-full h-9 rounded-lg border-2 border-[#3a1064] px-2 text-sm font-bold text-[#3a1064] mb-2"
          />
          <input
            data-testid="pin-note-input"
            value={pinNote}
            onChange={(e) => setPinNote(e.target.value)}
            placeholder="Optional note"
            className="w-full h-9 rounded-lg border-2 border-[#3a1064] px-2 text-sm font-bold text-[#3a1064] mb-2"
          />
          <div className="flex gap-2">
            <Button
              data-testid="confirm-pin-btn"
              onClick={confirmAddPin}
              className="flex-1 h-9 rounded-lg border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs shadow-[0_2px_0_#3a1064]"
            >
              <Plus className="h-3 w-3 mr-1" /> Add pin
            </Button>
            <Button
              variant="outline"
              onClick={() => setPendingDrop(null)}
              className="h-9 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] font-black text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
